import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";

interface RecentBlast {
  id: number;
  subject: string;
  sent_at: string | null;
  sent_count: number;
  tone: string;
}

interface OutboundStats {
  total_blasts_sent: number;
  total_emails_sent: number;
  avg_per_blast: number;
  recent_blasts: RecentBlast[];
}

interface SkmByPracticeArea {
  practice_area: string;
  total: number;
  responded: number;
  interested: number;
  response_rate: number;
}

interface SkmOverall {
  total_sent: number;
  total_responded: number;
  total_interested: number;
  response_rate: number;
  interest_rate: number;
}

interface SkmFollowUp {
  follow_up_due: number;
  follow_up_sent: number;
}

interface SkmPerformance {
  overall: SkmOverall;
  by_practice_area: SkmByPracticeArea[];
  follow_up_summary: SkmFollowUp;
}

interface Props {
  outbound_stats: OutboundStats;
  skm_performance: SkmPerformance;
}

function fmt(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function StatBox({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="bg-gray-50 rounded-lg p-3">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-xl font-bold text-gray-900 mt-0.5">{value}</p>
    </div>
  );
}

export default function OutboundAnalytics({ outbound_stats, skm_performance }: Props) {
  const overall = skm_performance.overall;
  const byPA    = skm_performance.by_practice_area.slice(0, 8);
  const maxRate = Math.max(...byPA.map((p) => p.response_rate), 1);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
      {/* Email blast performance */}
      <Card>
        <CardHeader><CardTitle className="text-base">Email Blast Performance</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <StatBox label="Blasts Sent"     value={outbound_stats.total_blasts_sent} />
            <StatBox label="Emails Sent"     value={outbound_stats.total_emails_sent} />
            <StatBox label="Avg / Blast"     value={outbound_stats.avg_per_blast} />
          </div>
          {outbound_stats.recent_blasts.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-4">No blasts sent yet</p>
          ) : (
            <div className="space-y-1">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Recent Blasts</p>
              {outbound_stats.recent_blasts.map((b) => (
                <div key={b.id} className="flex items-center justify-between text-xs py-1.5 border-b border-gray-100 last:border-0">
                  <span className="text-gray-700 truncate max-w-[180px]">{b.subject}</span>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-gray-400">{fmt(b.sent_at)}</span>
                    <span className="font-medium text-gray-700">{b.sent_count}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* SKM performance */}
      <div className="space-y-4">
        <Card>
          <CardHeader><CardTitle className="text-base">SKM Overview</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <StatBox label="Total Sent"     value={overall.total_sent} />
              <StatBox label="Response Rate"  value={`${overall.response_rate}%`} />
              <StatBox label="Interested"     value={overall.total_interested} />
              <StatBox label="Interest Rate"  value={`${overall.interest_rate}%`} />
            </div>
            <div className="flex items-center gap-3 text-xs text-gray-500">
              <span>Follow-up due: <strong className="text-amber-600">{skm_performance.follow_up_summary.follow_up_due}</strong></span>
              <span>Follow-up sent: <strong className="text-green-700">{skm_performance.follow_up_summary.follow_up_sent}</strong></span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">SKM by Practice Area</CardTitle></CardHeader>
          <CardContent className="space-y-2.5">
            {byPA.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-4">No SKM data yet</p>
            ) : (
              byPA.map((pa) => (
                <div key={pa.practice_area} className="flex items-center gap-3 text-sm">
                  <span className="w-24 text-right text-xs text-gray-500 shrink-0 truncate">{pa.practice_area}</span>
                  <div className="flex-1 bg-gray-100 rounded-full h-2">
                    <div className="bg-teal-500 h-2 rounded-full" style={{ width: `${(pa.response_rate / maxRate) * 100}%` }} />
                  </div>
                  <span className="w-10 text-right text-xs font-medium text-gray-700">{pa.response_rate}%</span>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
